import {
    ChangeDetectionStrategy,
    Component,
    computed,
    input,
    OnInit,
    output,
    Signal,
    ViewEncapsulation,
    inject,
    OnDestroy,
} from '@angular/core';
import { IQuizQuestionAnswer } from '@fi-app/fi-onboarding/fi-quiz/fi-quiz';
import {
    IOnboardingCompletionStep,
    IOnboardingCompletionUserStep,
    IQuizCompletion,
} from '../../fi-onboarding-completion';
import {
    FiOnboardingCompletionStateType,
    FiOnboardingQuizCompletionStateType,
    FiOnboardingStepReopenedReason,
} from '../../fi-onboarding-completion-enums';
import { isNullOrUndefined } from '@fi-app/shared/utils/is-null-or-undefined';
import { FiOnboardingCompletionSyncService } from '@fi-app/fi-onboarding/fi-onboarding-completion/fi-onboarding-completion-sync.service';
import { Subject } from 'rxjs';
import { FiOnboardingCompletionHelperService } from '../../fi-onboarding-completion-helper.service';
import { FiOnboardingStepCompletionDialogState } from '../../fi-onboarding-step-completion-dialog/fi-onboarding-step-completion-dialog-state.service';
@Component({
    selector: 'fi-quiz-completion',
    templateUrl: './quiz-completion.component.html',
    styleUrl: './quiz-completion.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-quiz-completion' },
    standalone: false,
})
export class FiQuizCompletionComponent implements OnInit, OnDestroy {
    public readonly step = input.required<IOnboardingCompletionStep>();
    protected readonly completionDialogState = inject(FiOnboardingStepCompletionDialogState);

    protected readonly isReviewMode: Signal<boolean> = this.completionDialogState.isReviewMode;

    public readonly enableSubmit = output<void>();
    public readonly quizStarted = output<void>();
    public readonly quizTimeout = output<void>();
    public readonly userCompletionStepChange = output<void>();

    private fiOnboardingCompletionSyncService = inject(FiOnboardingCompletionSyncService);
    private fiOnboardingCompletionHelperService = inject(FiOnboardingCompletionHelperService);

    protected readonly isQuizNotCompleted = computed(() => {
        const ucs = this.completionDialogState.userCompletionStep();
        return this.fiOnboardingCompletionHelperService.isQuizStepCompletionNotCompleted(ucs);
    });

    protected readonly quizCompletion = computed<IQuizCompletion | undefined>(
        () => this.completionDialogState.userCompletionStep().QuizCompletion,
    );

    protected readonly isQuizCompletedOrFailed = computed(() => {
        return this.isQuizCompleted() || this.isQuizFailed();
    });

    private readonly isQuizCompleted = computed(() => {
        return this.quizCompletion()?.CompletionState === FiOnboardingQuizCompletionStateType.Completed;
    });

    protected readonly isQuizFailed = computed(() => {
        return this.quizCompletion()?.CompletionState === FiOnboardingQuizCompletionStateType.Failed;
    });

    protected readonly isQuizInProgress = computed(() => {
        return this.quizCompletion()?.CompletionState === FiOnboardingQuizCompletionStateType.InProgress;
    });

    protected correctAnsweredQuestions = computed<IQuizQuestionAnswer[]>(() => {
        if (this.isReviewMode() || this.completionDialogState.isReportMode()) {
            if (this.isReportModeQuizNotCompletedOrInProgress()) {
                return [];
            }
            return this.completionDialogState.userCompletionStep().QuizCompletion.Answers.filter((answer) => {
                const question = this.step().Quiz.Questions.find((question) => question.Id === answer.QuizQuestionId);
                if (isNullOrUndefined(question)) {
                    return false;
                }
                const correctValues = question.PresetOptions.filter((option) => option.IsCorrect);
                return (
                    correctValues.every((option) => answer.Value?.includes(option.Value)) &&
                    answer.Value?.length === correctValues.length
                );
            });
        }

        return [];
    });

    protected readonly isReportModeQuizNotCompletedOrInProgress = computed(() => {
        return (
            this.completionDialogState.isReportMode() &&
            (this.quizCompletion()?.CompletionState === FiOnboardingQuizCompletionStateType.NotCompleted ||
                this.quizCompletion()?.CompletionState === FiOnboardingQuizCompletionStateType.InProgress)
        );
    });

    public ngOnInit(): void {
        this.subscribeOnCompletionChangedEvent();
    }

    protected onQuizInProgressUserCompletionChanged(next: IOnboardingCompletionUserStep): void {
        this.completionDialogState.setUserCompletionStep(next);
        this.userCompletionStepChange.emit();
    }

    private subscribeOnCompletionChangedEvent(): void {
        this.fiOnboardingCompletionSyncService.subscribeToOnboardingCompletionChanged({
            onboardingId: this.completionDialogState.stepCompletionConfig.onboardingId,
            userId: this.completionDialogState.stepCompletionConfig.userId,
            userCompletionSteps: [this.completionDialogState.userCompletionStep()],
            onDestroy$: this.ngOnDestroy$,
            callback: (updatedCompletions) => {
                if (updatedCompletions.length > 0 && this.isQuizInProgress()) {
                    // for preventing double timeout invoking
                    //console.log('quiz timeout SignalR event from backend');
                    this.quizTimeout.emit();
                }
            },
        });
    }

    protected retakeQuiz(): void {
        const userCompletionStep = this.completionDialogState.userCompletionStep();
        this.completionDialogState.setUserCompletionStep({
            ...userCompletionStep,
            CompletionState: FiOnboardingCompletionStateType.InProgress,
            QuizCompletion: {
                ...userCompletionStep.QuizCompletion,
                Answers: userCompletionStep.QuizCompletion.Answers.map((answer) => ({
                    ...answer,
                    Value: answer.IsDisabled ? answer.Value : [],
                })),
                CompletionIndex: userCompletionStep.QuizCompletion.CompletionIndex + 1,
                CompletionState: FiOnboardingQuizCompletionStateType.NotCompleted,
            },
        });
        this.completionDialogState.setSaveButtonHidden(false);
    }

    protected startQuiz(): void {
        const userCompletionStep = this.completionDialogState.userCompletionStep();
        this.completionDialogState.setUserCompletionStep({
            ...userCompletionStep,
            CompletionState: FiOnboardingCompletionStateType.InProgress,
            QuizCompletion: {
                ...userCompletionStep.QuizCompletion,
                CompletionState: FiOnboardingQuizCompletionStateType.InProgress,
                StartDateTime: this.step().Quiz.IsTimerEnabled ? new Date().toISOString() : undefined,
            },
        });
        this.quizStarted.emit();
    }

    private ngOnDestroy$ = new Subject<void>();
    public ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }

    protected readonly FiOnboardingQuizCompletionStateType = FiOnboardingQuizCompletionStateType;
}
